// cleanup_convert_to_typescript_file_CREATE_RECURRING_TRANSFERS

import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { FormContext } from '@glu/validation-react';

import BalanceDropdown, { accountBalServiceMapping } from 'components/balanceDrodown';
import usePlaceResolver from 'components/usePlaceResolver/usePlaceResolver';
import { getPermittedAccounts } from 'api/transfers/service';
import LocaleContext from 'components/locale/LocaleContext';
import { createDropdown } from 'components/singleTransfer/SingleTransfer.utils';
import { ACCOUNT_TYPES } from 'constants.js';

import { useStyles } from './recurringTransfer.styles';

export const AccountsComponent = ({ creditAccountId, debitAccountId, readOnly }) => {
  const { dropdown } = useStyles();
  const locale = useContext(LocaleContext);
  const services = useMemo(
    () => [
      getPermittedAccounts({
        showBalance: true
      })
    ],
    []
  );
  const { data, onChange: setValue } = useContext(FormContext);
  const {
    Placeholder,
    results: [permittedAccounts]
  } = usePlaceResolver({
    services
  });

  if (Placeholder) {
    return <Placeholder />;
  }

  const allAccounts = permittedAccounts.reduce(
    (acc, account) => {
      const { permissionType } = account;
      if ([ACCOUNT_TYPES.DEBIT, ACCOUNT_TYPES.BOTH].includes(permissionType)) {
        acc.debitAccounts.push(account);
      }

      if ([ACCOUNT_TYPES.CREDIT, ACCOUNT_TYPES.BOTH].includes(permissionType)) {
        acc.creditAccounts.push(account);
      }

      return acc;
    },
    { debitAccounts: [], creditAccounts: [] }
  );

  const debitAccountList = createDropdown(
    locale,
    allAccounts.debitAccounts,
    creditAccountId || data?.creditAccountId,
    ACCOUNT_TYPES.DEBIT
  );

  const creditAccountList = createDropdown(
    locale,
    allAccounts.creditAccounts,
    debitAccountId || data?.debitAccountId,
    ACCOUNT_TYPES.CREDIT
  );

  return (
    <>
      <BalanceDropdown
        service={accountBalServiceMapping.recurTransferDebit}
        disabled={readOnly}
        className={dropdown}
        labelText={locale['common.fromAccount']}
        options={debitAccountList}
        htmlId="debitAccount"
        name="debitAccount"
        onChange={(name, value) => {
          setValue('debitAccountId', value?.[0]?.id, false);
        }}
        forcePositionY
        defaultSelectMessage={locale['common.select']}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
      <BalanceDropdown
        service={accountBalServiceMapping.recurTransferCredit}
        getBalanceLabel={(data, id) => data.find((item) => item.id === id)?.balanceLabel}
        disabled={readOnly}
        className={dropdown}
        labelText={locale['common.toAccount']}
        options={creditAccountList}
        htmlId="creditAccount"
        name="creditAccount"
        onChange={(name, value) => {
          setValue('creditAccountId', value?.[0]?.id, false);
        }}
        forcePositionY
        defaultSelectMessage={locale['common.select']}
        validators={[{ required: true, msg: locale['validation.required'] }]}
      />
    </>
  );
};

AccountsComponent.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  debitAccountId: PropTypes.number.isRequired,
  creditAccountId: PropTypes.number.isRequired
};

export default AccountsComponent;
